import { guesswords } from "./guesswords";
import "svelte/store";
let canSuggestRemotely = true;
/*
  Refactoring idea:

  1. Dictionary -> GuessSet
  2. answersDictionary -> Dictionary class
  3. XorshiftPrng -> inside Dictionary
  4. Remote dictionary cannot participate in Daily Slovl (only bundled words)
  5. Bundled words (Dictionary can be unsorted; and shuffled by 64-words sections)
  6. Words go by 256-words sections and Daily Slovl is (see below)
     generating a stable sequence that survives adding new sections to bundle

     In other words, we shuffle each section of Dictionary according to initial_seed,
     obtain current section by floor(day_from_slovl_epoch/64),
     obtain current slovl of the day by index (day_from_slovl_epoch mod 64).
     
     Players can backsolve previous days by setting day_from_slovl_epoch to 1, 2, 3...
*/
export class GuessDictionary {
    constructor(answers) {
        this.remoteSet = new Set();
        this.localWords = [];
        this.guessSet = new Set(answers.concat(guesswords));
        this.restoreLocal();
        this.fetch();
    }
    restoreLocal() {
        this.localWords = typeof window !== "undefined" && 'localStorage' in window ? (localStorage.getItem("SLOVL_LOCAL") || "").split(",").filter(s => !!s) : [];
    }
    pushLocal(w) {
        this.localWords.push(w);
        this.guessSet.add(w);
        this.persistLocal();
    }
    persistLocal() {
        this.localWords = this.localWords.filter(s => !!s && !this.remoteSet.has(s));
        localStorage.setItem("SLOVL_LOCAL", this.localWords.join(","));
    }
    fetch() {
        if (canSuggestRemotely && typeof fetch !== "undefined") {
            this.req = fetch(`/slovl/api/words`)
                .then(r => r.json())
                .then(r => r.words)
                .then(words => {
                this.remoteSet = new Set(words);
                for (let w of words) {
                    this.guessSet.add(w);
                }
                this.persistLocal();
                return words;
            })
                .catch((err) => {
                console.log("Remote", err);
                return [];
            });
        }
        else {
            this.req = Promise.resolve([]);
        }
        return this.req;
    }
    get length() {
        return this.guessSet.size;
    }
    add(a) {
        for (let w of a) {
            this.guessSet.add(w);
        }
    }
    has(w) {
        return this.guessSet.has(w);
    }
    suggest(w) {
        this.pushLocal(w);
        if (canSuggestRemotely) {
            fetch(`/slovl/api/suggest-word?word=${encodeURIComponent(w)}`)
                .catch(err => console.error(err));
        }
    }
}
export class AnswersDictionary {
    constructor(init, rng) {
        this.currentPermutationIdx = 0;
        this.words = init;
        this.rng = rng;
        this.wordsSet = new Set(this.words);
    }
    get length() {
        return this.words.length;
    }
    has(w) {
        return this.wordsSet.has(w);
    }
    advance() {
        let idx = this.currentPermutation[this.currentPermutationIdx++];
        let w = this.words[idx];
        if (this.currentPermutationIdx === this.currentPermutation.length) {
            this.resetPermutation();
        }
        return w;
    }
    generatePermutation(len) {
        this.generationParameters = [this.rng.query_state(), len];
        let indices = new Array(len);
        for (let i = 0; i < len; i++)
            indices[i] = i;
        // Fisher-Yates; TODO checkout Durstenfeld to avoid loop above
        for (let i = len - 1; i > 0; i--) {
            let j = this.rng.rand(0, i + 1);
            let tmp = indices[j];
            indices[j] = indices[i];
            indices[i] = tmp;
        }
        return indices;
    }
    resetPermutation() {
        this.currentPermutation = this.generatePermutation(this.words.length);
        this.currentPermutationIdx = 0;
    }
    accordingToPermutation() {
        return this.currentPermutation.map((idx) => this.words[idx]);
    }
    restorePermutation([seed, len, idx]) {
        this.rng.seed(seed);
        this.currentPermutation = this.generatePermutation(len);
        this.currentPermutationIdx = idx - 1;
    }
}
